"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDynamicLinkRedirect = getDynamicLinkRedirect;
exports.DynamicLinkRedirect = DynamicLinkRedirect;
const hubApi_1 = require("../utils/hubApi");
const DEFAULT_MODEL_URL_MAPPINGS = {
    article: (slug) => `/content-hub/${slug}`,
    "clinical-trial": (slug) => `/clinical-trials/find/${slug}`,
    location: (slug) => `/facilities/${slug}`,
};
const CORP_API_MODELS = ["article", "clinical-trial", "provider"];
const SLUG_BASED_MODELS = ["article", "clinical-trial", "location"];
function toGraphQLModelName(model) {
    return model.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}
async function fetchContent(endpoint, graphQLQuery, model) {
    const url = `${endpoint}?query=${encodeURIComponent(graphQLQuery)}`;
    const response = await fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
        },
    });
    const { data } = await response.json();
    const record = data?.[model]?.[0]?.data;
    if (!record)
        return null;
    return { url: record.url, slug: record.slug };
}
async function fetchLocationByServiceId(endpoint, serviceLocationId) {
    const numericId = parseInt(serviceLocationId, 10);
    if (isNaN(numericId))
        return null;
    for (const field of ["serviceLocationId", "locationId"]) {
        const query = `query { location(query: { data: { ${field}: ${numericId} } }) { data { slug } } }`;
        const result = await fetchContent(endpoint, query, "location");
        if (result?.slug)
            return result;
    }
    return null;
}
async function getDynamicLinkRedirect(params, config) {
    const { model, type, id } = params;
    const { defaultApiKey, corpApiKey, modelUrlMappings } = config;
    if (!id || !model || !type) {
        return { notFound: true };
    }
    try {
        const apiKey = CORP_API_MODELS.includes(model)
            ? (corpApiKey || defaultApiKey)
            : defaultApiKey;
        if (!apiKey) {
            throw new Error("Builder.io API key not found");
        }
        const endpoint = `https://cdn.builder.io/api/v3/graphql/${apiKey}`;
        let pageUrl = null;
        if (type === "sitecore") {
            const buildPageSitecoreQuery = (sitecoreId) => `
      query {
        page(query: { data: { metadata: { sitecoreId: "${sitecoreId}" } } }) {
          data {
            url
          }
        }
      }
      `.trim();
            const buildLocationSitecoreQuery = (sitecoreId) => `
      query {
        location(query: { data: { metadata: { sitecoreId: "${sitecoreId}" } } }) {
          data {
            slug
            url
          }
        }
      }
      `.trim();
            let result = await fetchContent(endpoint, buildPageSitecoreQuery(id), "page");
            // Fallback: some entries may have been stored with lowercase IDs in Builder.io
            if (!result && id !== id.toLowerCase()) {
                result = await fetchContent(endpoint, buildPageSitecoreQuery(id.toLowerCase()), "page");
            }
            pageUrl = result?.url || null;
            if (!pageUrl) {
                let locationResult = await fetchContent(endpoint, buildLocationSitecoreQuery(id), "location");
                if (!locationResult && id !== id.toLowerCase()) {
                    locationResult = await fetchContent(endpoint, buildLocationSitecoreQuery(id.toLowerCase()), "location");
                }
                if (locationResult) {
                    const urlMappings = { ...DEFAULT_MODEL_URL_MAPPINGS, ...modelUrlMappings };
                    const locationUrlMapper = urlMappings.location;
                    if (locationUrlMapper && locationResult.slug) {
                        pageUrl = locationUrlMapper(locationResult.slug);
                    }
                    else if (locationResult.url) {
                        pageUrl = locationResult.url;
                    }
                }
            }
        }
        else if (type === "builder") {
            if (model === "location") {
                // Location referenceIds are Hub serviceLocationIds (numeric), not Builder.io content IDs.
                // First try to find the Builder.io location entry by serviceLocationId/locationId.
                const locationResult = await fetchLocationByServiceId(endpoint, id);
                if (locationResult?.slug) {
                    pageUrl = `/facilities/${locationResult.slug}`;
                }
                else {
                    // Fall back to Hub API if location not yet in Builder.io
                    try {
                        const hubLocation = await (0, hubApi_1.hubApiGet)(`servicelocations/${id}`);
                        if (hubLocation?.slug) {
                            pageUrl = `/facilities/${hubLocation.slug}`;
                        }
                    }
                    catch {
                        // Hub API unavailable or location not found — leave pageUrl null
                    }
                }
            }
            else {
                const useSlug = SLUG_BASED_MODELS.includes(model);
                const dataFields = useSlug ? "slug" : "url";
                const graphQLModel = toGraphQLModelName(model);
                let graphQLQuery = `
        query {
          ${graphQLModel}(query: { id: "${id}" }) {
            data { ${dataFields} }
          }
        }
        `.trim();
                let result = await fetchContent(endpoint, graphQLQuery, graphQLModel);
                if (!result) {
                    const prefixedId = `${apiKey}_${id}`;
                    graphQLQuery = `
          query {
            ${graphQLModel}(query: { id: "${prefixedId}" }) {
              data { ${dataFields} }
            }
          }
          `.trim();
                    result = await fetchContent(endpoint, graphQLQuery, graphQLModel);
                }
                if (result) {
                    const urlMappings = { ...DEFAULT_MODEL_URL_MAPPINGS, ...modelUrlMappings };
                    const urlMapper = urlMappings[model];
                    if (urlMapper && result.slug) {
                        pageUrl = urlMapper(result.slug);
                    }
                    else if (result.url) {
                        pageUrl = result.url;
                    }
                }
            }
        }
        if (pageUrl) {
            return {
                redirect: {
                    destination: pageUrl,
                    permanent: false,
                },
            };
        }
        return { notFound: true };
    }
    catch (error) {
        console.error("Error fetching content:", error);
        return { notFound: true };
    }
}
function DynamicLinkRedirect() {
    return null;
}
